import React, { ChangeEvent, Component } from 'react'
import { Form, Col } from 'react-bootstrap'
import { ScheduleModel } from './OrderModels'
import CollectionSite from "../../api/Location/LocationModel/CollectionSite"
import { stateAbbreviationsToCommonWealthState, StateToTimeZone } from "../../api/Location/LocationModel/CommonWealthStateTextMapping"
import Checkbox from '../Checkbox'
import { OrderDefault } from '../../api/Administration/OrderDefaults'
import CalculateDateStrings from './CalculateDateStrings'
import ExpirationDate from '../ExpirationDate'

export interface ScheduleProps {
  allowDonorSelect: boolean,
  schedule: ScheduleModel,
  clinic?: CollectionSite,
  defaultExpiration?: OrderDefault
  onScheduleChange: (schedule: ScheduleModel) => void
}
const DefaultExtendExpirationHours: number = 24
const DeafultExtendExpirationMS: number = DefaultExtendExpirationHours * 60 * 60 * 1000
const ExpirationDateChangeMarker = -999999999
export default class Schedule extends Component<ScheduleProps> {
  // TODO: Change to get date from clinic's coordinates
  //
  // Current implementation of Timezone and Estimated Expiration Date calculation is a sample
  // implementation and relies on browser local datetime because we cannot obtain local time of
  // selected clinic due to blocker described in GitHub issue #5 (https://github.com/chcinc1/web-crl-workforce/issues/5)
  //
  state: ScheduleModel = { ...this.props.schedule }

  handleOverrideAllowedChange = (overrideAllowed: boolean) => {
    this.updateState({ overrideAllowed: overrideAllowed }, true)
  }

  handleHoursToExpireChange = (value: string) => {
    var r = value.replace(/\D/g, "") // Only digits
    r = r.length > 3 ? r.slice(0, 3) : r // Maxlength 3 digits
    this.updateState({
      hoursUntilExpires: !!r ? +r : undefined
    })
  }

  handleChange = (name: string, value: string) => {
    this.updateState({ [name]: value })
  }

  updateState = (changes: object, sendSchedule: boolean = false) => {
    this.setState((prevState) => { return { ...prevState, ...changes } }, () => { if (sendSchedule) { this.sendSchedule() } })
  }

  sendSchedule = () => {
    this.props.onScheduleChange(this.state)
  }

  calculateExpirationDate = (prevExpirationDate: Date | undefined, expirationHours: number = 0) => {
    const estimatedExpiration = prevExpirationDate || new Date()
    let expirationMS: number = 0
    if (expirationHours !== ExpirationDateChangeMarker) {
      expirationMS = expirationHours * 60 * 60 * 1000
    }
    if (this.state.overrideAllowed) {
      expirationMS = expirationMS + DeafultExtendExpirationMS
    }
    else {
      if (expirationHours !== ExpirationDateChangeMarker) {
        expirationMS = expirationMS - DeafultExtendExpirationMS
      }
    }
    const expirationDate = expirationMS !== 0
      ? new Date(estimatedExpiration.getTime() + expirationMS)
      : prevExpirationDate

    let formattedExpirationDate = ""
    let displayExpirationDate = ""

    if (expirationDate) {
      const dateStrings = CalculateDateStrings(expirationDate, this.state.timezone || "")
      formattedExpirationDate = dateStrings.formattedDate
      displayExpirationDate = dateStrings.gmtFullDate
    }

    this.updateState({ expirationDate: expirationDate, formattedExpirationDate, displayExpirationDate }, true)
  }

  initializeTimezone = () => {
    this.updateTimeZone(this.props.clinic)
  }

  componentDidMount() {
    this.initializeTimezone()
  }

  componentDidUpdate(prevProps: ScheduleProps) {
    if (prevProps.clinic !== this.props.clinic) {
      this.updateTimeZone(this.props.clinic)
    }
    const defaultExpirationHours = +(this.props.defaultExpiration?.DefaultValue || 0)
    if (prevProps.schedule.timezone !== this.props.schedule.timezone) {
      this.calculateExpirationDate(new Date(), defaultExpirationHours)
    }
    if ((prevProps.defaultExpiration !== this.props.defaultExpiration) || (prevProps.allowDonorSelect !== this.props.allowDonorSelect)) {
      this.handleHoursToExpireChange(this.props.defaultExpiration?.DefaultValue || "")
      this.calculateExpirationDate(new Date(), defaultExpirationHours)
    }

    if (prevProps.schedule.overrideAllowed !== this.props.schedule.overrideAllowed) {
      this.calculateExpirationDate(this.props.schedule.expirationDate, 0)
    }
  }

  updateTimeZone = (clinic?: CollectionSite) => {
    let timezone = undefined
    if (clinic?.State) {
      const state = stateAbbreviationsToCommonWealthState[clinic?.State]
      timezone = clinic.TimeZone || StateToTimeZone[state]
    }
    this.updateState({ timezone: timezone }, true)
  }

  proceedImmediatelyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const enabled = !!e.target.checked
    this.updateState({ proceedImmediately: enabled }, true)
  }

  render() {
    const { expirationDate, overrideAllowed, comment, timezone, proceedImmediately } = this.state
    return (
      <>
        <Form className="py-2" hidden={!this.props.clinic && !this.props.allowDonorSelect}>
          <Form.Row>
            <Form.Label column className="font-weight-bold">SCHEDULE</Form.Label>
          </Form.Row>
          {this.props.clinic && <Form.Row>
            <Col xs={12} sm={3}>
              <Form.Label className="font-weight-bold">Clinic Time Zone</Form.Label>
              <Form.Control type="text" readOnly={true} value={timezone || ''} />
            </Col>
          </Form.Row>}
          <Form.Row>
            <Col xs={12} sm={6} md={3}>
              <Checkbox id="proceedImmediately" label="Hide Expiration Date" className="font-weight-bold" checked={proceedImmediately} onChange={(e) => { this.proceedImmediatelyChange(e) }} />
            </Col>
          </Form.Row>
          {!proceedImmediately && <Form.Row>
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="font-weight-bold">Expiration Date</Form.Label>
              <ExpirationDate expirationDate={expirationDate as Date} timezone={timezone} onChange={date => this.calculateExpirationDate(date as Date || undefined, ExpirationDateChangeMarker)} />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Checkbox id="override_allowed" label="Extend Expiration by 24 Hours" className="font-weight-bold" checked={overrideAllowed} onChange={(e) => { this.handleOverrideAllowedChange(e.target.checked) }} />
            </Col>
          </Form.Row>}
          <Form.Row>
            <Col xs={12} sm={6}>
              <Form.Label className="font-weight-bold">COMMENTS TO SITE</Form.Label>
              <Form.Control as="textarea"
                rows={3}
                value={comment}
                onChange={e => this.handleChange('comment', e.target.value)}
                onBlur={() => this.sendSchedule()} />
            </Col>
          </Form.Row>
        </Form>
      </>
    )
  }
}
